import { PING_LEAD_DAYS } from "@larder/shared";
import type { AskContext } from "../push/usePush";

/**
 * The soft ask, shown in context and never cold.
 *
 * Two things about this screen are load-bearing, and both are about the fact
 * that the browser's permission dialog can be refused exactly ONCE, forever.
 *
 * 1. **It is not the browser dialog.** Tapping "not now" here costs nothing:
 *    the native prompt is never fired, so the permission stays at `default` and
 *    can be asked for again in a week. Firing the real dialog first and hoping
 *    would spend the one refusal the user is allowed on a moment we chose badly.
 *
 * 2. **It appears at a moment that has already made the argument.** Right after
 *    someone puts a date on something, or opens something with a date. At that
 *    instant the question answers itself, and the copy can name the actual item
 *    rather than talking about notifications in the abstract. See
 *    docs/decisions/0020-web-push-over-vapid.md.
 *
 * The sell is the product, not the feature. Nobody wants notifications; they
 * want to not throw away paneer.
 */
export function NotifyPrimer({
  context,
  onAllow,
  onDismiss,
  busy,
}: {
  context: AskContext;
  onAllow: () => void;
  onDismiss: () => void;
  busy: boolean;
}) {
  return (
    <>
      <div className="fabscrim" aria-hidden="true" onClick={onDismiss} />
      <div
        className="rebuy notify-primer"
        role="dialog"
        aria-modal="true"
        aria-label="Get reminded before things go off"
        data-testid="notify-primer"
      >
        <button
          type="button"
          className="rebuy-close"
          onClick={onDismiss}
          aria-label="Not now"
          data-testid="notify-close"
        >
          ✕
        </button>

        <span className="notify-primer-glyph" aria-hidden="true">
          🔔
        </span>

        <p className="rebuy-title">{title(context)}</p>

        <p className="note">
          One reminder, {PING_LEAD_DAYS} days before something goes off. That's the whole
          job — Larder can't warn you if it can't reach you.
        </p>

        {/* The honest counterweight. Saying what we WON'T send is what makes the
            yes cheap, and it is a promise the sender actually keeps: the
            scheduled function sends for check-ins and nothing else. */}
        <p className="note dim notify-primer-promise">
          Nothing else. No marketing, no daily digest, and you can turn it off in
          Settings.
        </p>

        <div className="rebuy-actions two">
          <button
            type="button"
            className="answer good"
            disabled={busy}
            onClick={onAllow}
            data-testid="notify-allow"
          >
            Yes, remind me
          </button>
          <button
            type="button"
            className="answer neutral"
            disabled={busy}
            onClick={onDismiss}
            data-testid="notify-later"
          >
            Not now
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * Name the thing and, where possible, the deadline.
 *
 * A countdown is only quoted when it is genuinely known and genuinely soon.
 * "Goes off in 47 days" is technically true and rhetorically useless, and
 * quoting a number for an item whose date we are unsure of would be the app
 * inventing precision — the one thing this product refuses to do everywhere
 * else (AGENTS.md rule 2).
 */
function title({ name, daysLeft }: AskContext): React.ReactNode {
  if (daysLeft !== null && daysLeft >= 0 && daysLeft <= 14) {
    // Something going off TODAY is past the point a reminder could have helped,
    // so the ask is honestly framed as being about the next one rather than
    // implying we could still have saved this.
    return daysLeft === 0 ? (
      <>
        Your <strong>{name}</strong> goes off today. Want a heads-up next time?
      </>
    ) : (
      <>
        Your <strong>{name}</strong> goes off in {days(daysLeft)}. Want a heads-up?
      </>
    );
  }
  return (
    <>
      Want a heads-up before your <strong>{name}</strong> goes off?
    </>
  );
}

function days(n: number): string {
  return n === 1 ? "a day" : `${n} days`;
}
